import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  ChargeableItem,
  Connection,
  OrderChargeableItem,
  PaymentRequest,
  Service,
} from '@quality24/inpatient-typings';

import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router';

import {
  Button,
  ProgressTracker,
  ProgressTrackerProps,
  PullToRefresh,
  Spinner,
  Text,
  TrackerStep,
  useToast,
} from '@quality24/design-system';
import {
  faCheck,
  faHourglassHalf,
  faMoneyBill,
  faX,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import moment from 'moment';
import { v4 as uuid } from 'uuid';
import ApiError from '@/inpatient-patient-pwa/molecules/ApiError';
import PaymentStatusBadge from '@/inpatient-patient-pwa/atoms/PaymentStatusBadge';

export const FETCH_ALERT_WITH_PAYMENT_QUERY = gql`
  query fetchAlertWithPaymentData($id: UUID!) {
    alert: alertById(id: $id) {
      id
      createdAt
      resolvedAt
      cancelledAt
      details
      # Same service (and parent) the API reads to build paymentRequest.name
      service: reason {
        id
        reasonI18N
        parent {
          id
          reasonI18N
        }
      }
      paymentRequest {
        id
        name
        price
        url
        status
        paidAt
        orderNumber
      }
      orderChargeableItems {
        nodes {
          id
          name
          price
          quantity
          # The name above is the pt-BR snapshot taken when the order was
          # placed, so the translation comes from the item it points to.
          chargeableItem: item {
            id
            nameI18N
          }
        }
      }
    }
  }
`;

export const CANCEL_ORDER_MUTATION = gql`
  mutation cancelAlertWithActions(
    $input: CancelAlertWithActionsInput!
    $paymentRequestId: UUID!
  ) {
    cancelAlertWithActions(input: $input) {
      clientMutationId
    }
    updatePaymentRequestById(
      input: { patch: { status: VOIDED }, id: $paymentRequestId }
    ) {
      clientMutationId
    }
  }
`;

export interface QueryResult {
  alert: Pick<
    Alert,
    'id' | 'createdAt' | 'resolvedAt' | 'cancelledAt' | 'details'
  > & {
    service: Pick<Service, 'id' | 'reasonI18N'> & {
      parent: Pick<Service, 'id' | 'reasonI18N'> | null;
    };
    paymentRequest: Pick<
      PaymentRequest,
      'id' | 'name' | 'price' | 'url' | 'status' | 'paidAt' | 'orderNumber'
    >;
    orderChargeableItems: Connection<
      Pick<OrderChargeableItem, 'id' | 'name' | 'price' | 'quantity'> & {
        chargeableItem: Pick<ChargeableItem, 'id' | 'nameI18N'> | null;
      }
    >;
  };
}

/**
 * Translated order name, mirroring how the API builds `payment_request.name`:
 * `parent reason (reason)`, or just `reason` when the service has no parent.
 *
 * The stored name is a pt-BR snapshot built by the backend in
 * `apps/inpatient-api/src/schema/plugins/payment/PaymentPlugin.ts`. If the
 * format changes there, update it here as well.
 */
export const formatOrderName = (
  service: QueryResult['alert']['service'] | null | undefined,
): string | null => {
  if (!service?.reasonI18N) return null;
  return service.parent?.reasonI18N
    ? `${service.parent.reasonI18N} (${service.reasonI18N})`
    : service.reasonI18N;
};

/**
 * Status of the "in progress" step: it starts once the payment is approved and
 * ends when the order is resolved.
 */
const getInProgressState = (
  order: QueryResult['alert'],
): Pick<TrackerStep, 'status' | 'icon'> => {
  if (order.resolvedAt) return { status: 'complete', icon: faCheck };
  if (order.paymentRequest.paidAt) {
    return { status: 'active', icon: faHourglassHalf };
  }
  return { status: 'pending' };
};

export const formatBRL = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const OrderDetailsPage = (): JSX.Element => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const navigate = useNavigate();

  const [cancelOrder] = useMutation(CANCEL_ORDER_MUTATION);
  const { data, loading, error, refetch } = useQuery<QueryResult>(
    FETCH_ALERT_WITH_PAYMENT_QUERY,
    { variables: { id } },
  );

  const order = React.useMemo(() => data?.alert, [data?.alert]);

  /**
   * Formats a timestamp with the localized date and time patterns — the moment
   * locale is kept in sync with the app language in `src/i18n/sideEffects.ts`.
   */
  const formatDateTime = React.useCallback(
    (value: string) =>
      t('order.details.dateAtTime', {
        date: moment(value).format('L'),
        time: moment(value).format('LTS'),
      }),
    [t],
  );

  const steps: ProgressTrackerProps['steps'] = React.useMemo(
    () =>
      order
        ? [
            {
              id: 'started',
              title: t('order.details.placed'),
              status: 'complete',
              icon: faCheck,
              content: {
                '': formatDateTime(order.createdAt),
              },
            },
            {
              id: 'paid',
              title: t('order.details.paymentApproved'),
              status: order.paymentRequest.paidAt ? 'complete' : 'active',
              icon: order.paymentRequest.paidAt ? faCheck : faHourglassHalf,
              content: {
                '': order.paymentRequest.paidAt
                  ? formatDateTime(order.paymentRequest.paidAt)
                  : undefined,
              },
            },
            // The solution flow steps are summarised as a single one: their
            // names come from the database and have no translation.
            ...(!order.cancelledAt
              ? [
                  {
                    id: 'in-progress',
                    title: t('order.details.inProgress'),
                    ...getInProgressState(order),
                  } as TrackerStep,
                ]
              : []),
            ...(order.cancelledAt
              ? [
                  {
                    id: uuid(),
                    icon: faX,
                    status: 'complete',
                    title: t('order.details.cancelled'),
                    content: {
                      [t('order.details.date')]: formatDateTime(
                        order.cancelledAt,
                      ),
                    },
                  } as TrackerStep,
                ]
              : []),
            ...(!order.cancelledAt
              ? [
                  {
                    id: 'finished',
                    title: t('order.details.closed'),
                    status: order.resolvedAt ? 'complete' : 'pending',
                    icon: order.resolvedAt ? faCheck : undefined,
                  } as TrackerStep,
                ]
              : []),
          ]
        : [],
    [order, t, formatDateTime],
  );

  const handleCancelOrder = React.useCallback(async () => {
    try {
      await cancelOrder({
        variables: {
          input: {
            alertId: order?.id,
            alertDetails: {
              ...order?.details,
              cancelReason: 'Cancelado pelo solicitante',
            },
          },
          paymentRequestId: order?.paymentRequest.id,
        },
      });
      navigate('/calls');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      toast.error(t('errors.cancelOrderFailed'));
    }
  }, [
    cancelOrder,
    navigate,
    order?.details,
    order?.id,
    order?.paymentRequest.id,
    toast,
    t,
  ]);

  return (
    <div className="d-flex flex-column gap-3">
      <Helmet>
        <title>{t('order.details.pageTitle')}</title>
      </Helmet>

      {loading && (
        <div className="d-flex flex-justify-center p-4">
          <Spinner />
        </div>
      )}

      {error && (
        <div className="d-flex">
          <ApiError error={error} refetch={refetch} />
        </div>
      )}

      {!loading && !error && (
        <PullToRefresh onRefresh={refetch}>
          <div className="px-4 d-flex flex-column gap-3">
            <div className="d-flex flex-justify-between">
              <div>
                <Text weight="semiBold" id="order-number-label">
                  {t('order.details.order')}
                </Text>
                <Text aria-labelledby="order-number-label">
                  {formatOrderName(order?.service) ||
                    order?.paymentRequest.name ||
                    '-'}
                </Text>
              </div>
              {order?.paymentRequest.status && !order.cancelledAt && (
                <PaymentStatusBadge
                  status={order.paymentRequest.status}
                  className="text-center"
                />
              )}
            </div>
            <div className="d-flex flex-column gap-2">
              <Text weight="semiBold">{t('order.details.items')}</Text>
              {order?.orderChargeableItems.nodes.map((item) => (
                <div className="d-flex flex-justify-between" key={item.id}>
                  <Text id={`item-label-${item.id}`}>{`(${item.quantity}) ${
                    item.chargeableItem?.nameI18N || item.name
                  }`}</Text>
                  <Text weight="bold" aria-labelledby={`item-label-${item.id}`}>
                    {formatBRL.format(item.price / 100)}
                  </Text>
                </div>
              ))}
              <div className="d-flex flex-justify-between">
                <Text weight="bold" id="total-value-label">
                  {t('order.details.total')}
                </Text>
                {order?.paymentRequest.price && (
                  <Text
                    weight="bold"
                    aria-labelledby="total-value-label"
                    aria-label={t('order.details.totalValue')}
                  >
                    {formatBRL.format(order.paymentRequest.price / 100)}
                  </Text>
                )}
              </div>
            </div>
          </div>
          <div className="bg-white my-3">
            <div className="p-4">
              <ProgressTracker steps={steps} vertical />
            </div>
          </div>

          {!order?.paymentRequest.paidAt &&
            !order?.cancelledAt &&
            !order?.resolvedAt && (
              <div className="px-4 d-flex flex-column gap-3">
                <Button
                  fluid
                  icon={faXmark}
                  variant="outline"
                  color="danger"
                  onClick={handleCancelOrder}
                >
                  {t('order.details.cancelOrder')}
                </Button>

                <Button
                  fluid
                  icon={faMoneyBill}
                  onClick={() => window.open(order?.paymentRequest.url || '#')}
                >
                  {t('order.details.payNow')}
                </Button>
              </div>
            )}
        </PullToRefresh>
      )}
    </div>
  );
};

export default OrderDetailsPage;
